import {
  keepPreviousData,
  useQueries,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { Suspense, useState } from "react";
import { customerQuery } from "@/api/customers";
import { ApiError } from "@/api/errors";
import { establishmentQuery } from "@/api/establishment";
import { professionalsQuery } from "@/api/professionals";
import { servicesQuery } from "@/api/services";
import { deleteWaitlistEntry, type WaitlistEntry, waitlistQuery } from "@/api/waitlist";
import { warmQueries } from "@/api/warm";
import { useAuth } from "@/auth/AuthProvider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
} from "@/components/ui/responsive-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { zonedDate, zonedTime } from "@/lib/datetime";
import { notifySuccess } from "@/lib/feedback";

export const Route = createFileRoute("/_authed/fila")({
  loader: ({ context: { queryClient, establishmentId } }) =>
    warmQueries(queryClient, [
      establishmentQuery(establishmentId),
      servicesQuery(establishmentId),
      professionalsQuery(establishmentId),
    ]),
  staticData: {
    title: "Fila de espera",
    subtitle:
      "Quando abre uma vaga, o bot oferece pra quem está aqui, na ordem de entrada.",
  },
  component: FilaPage,
});

/** Valor do Select pra "sem filtro" (Radix não aceita value vazio). */
const ALL = "__todos__";

/** `YYYY-MM-DD` → `DD/MM/YYYY` sem passar por Date (a data é wall-clock, sem fuso). */
function formatDate(date: string): string {
  return date.split("-").reverse().join("/");
}

/** "24/03/2026 09:00" — data e hora sempre no fuso DO ESTABELECIMENTO. */
function formatInstant(iso: string, timeZone: string): string {
  const [year, month, day] = zonedDate(iso, timeZone).split("-");
  return `${day}/${month}/${year} ${zonedTime(iso, timeZone)}`;
}

function FilaPage() {
  const { user } = useAuth();
  // `_authed` só renderiza autenticado — user nunca é null aqui.
  if (!user) return null;
  return (
    <Suspense fallback={<FilaSkeleton />}>
      <FilaContent establishmentId={user.establishment_id} />
    </Suspense>
  );
}

function FilaSkeleton() {
  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6">
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-72 w-full" />
    </div>
  );
}

/**
 * Fila de espera: página de leitura + remoção. Quem alimenta a fila é o bot
 * do WhatsApp, e a conversão da vaga é automática (o cliente confirma no
 * WhatsApp) — por isso não existe ação de "converter" aqui.
 */
function FilaContent({ establishmentId }: { establishmentId: string }) {
  const queryClient = useQueryClient();
  const { data: establishment } = useSuspenseQuery(establishmentQuery(establishmentId));
  const { data: services } = useSuspenseQuery(servicesQuery(establishmentId));
  const { data: professionals } = useSuspenseQuery(professionalsQuery(establishmentId));
  const timeZone = establishment.timezone;

  const [professionalFilter, setProfessionalFilter] = useState(ALL);
  const [serviceFilter, setServiceFilter] = useState(ALL);

  // `useQuery` + `keepPreviousData` (não suspense) pelo mesmo motivo do
  // UpcomingAppointments: trocar o filtro não derruba a tabela inteira no
  // skeleton do Suspense — a lista anterior fica visível até a nova chegar.
  const waitlist = useQuery({
    ...waitlistQuery(establishmentId, {
      professionalId: professionalFilter === ALL ? undefined : professionalFilter,
      serviceId: serviceFilter === ALL ? undefined : serviceFilter,
    }),
    placeholderData: keepPreviousData,
  });

  const serviceNames = new Map(services.map((s) => [s.id, s.name] as const));
  const professionalNames = new Map(professionals.map((p) => [p.id, p.name] as const));

  // A API já devolve as entradas em `created_at` ASC (a ordem da fila — o
  // repositório usa Order("created_at")), então aqui NÃO se reordena.
  const entries = waitlist.data ?? [];

  // Nome do cliente não vem na entrada. `GET /customers` (busca paginada)
  // existe na API — falta a tela de listagem; aqui não caberia busca por
  // texto, então resolve um a um e o cache dedupida os repetidos (mesma
  // solução da agenda).
  const customerIds = [...new Set(entries.map((e) => e.customer_id))];
  const customerResults = useQueries({
    queries: customerIds.map((id) => customerQuery(id)),
  });
  const customerNames = new Map<string, string>();
  customerIds.forEach((id, i) => {
    const name = customerResults[i]?.data?.name;
    if (name) customerNames.set(id, name);
  });

  // Confirmação em diálogo, não inline na linha: a remoção é irreversível
  // (o cliente deixa de receber ofertas) e o aviso inline crescia a coluna
  // de ações a ponto de empurrar o próprio botão de confirmar pra fora da
  // tela. O diálogo também é o padrão do resto do painel.
  const [confirming, setConfirming] = useState<WaitlistEntry | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  async function onRemove() {
    if (!confirming) return;
    setRemoveError(null);
    setRemoving(true);
    try {
      await deleteWaitlistEntry(confirming.id);
      notifySuccess("Saiu da fila de espera");
      setConfirming(null);
    } catch (e) {
      setRemoveError(
        e instanceof ApiError && e.code === "waitlist_entry_not_found"
          ? "Esta entrada já não está mais na fila — a lista foi atualizada."
          : e instanceof ApiError
            ? e.message
            : "Não foi possível remover. Tente de novo.",
      );
      setConfirming(null);
    } finally {
      setRemoving(false);
      // Revalida em qualquer desfecho, inclusive no erro: o motivo mais
      // comum de falhar é a entrada já ter saído da fila (o cliente
      // aceitou a vaga, outra aba removeu), e aí a linha que sobrou na
      // tela é justamente a que não existe mais.
      await queryClient.invalidateQueries({
        queryKey: ["waitlist", establishmentId],
      });
    }
  }

  return (
    <>
      {/* `max-w-7xl`, não 5xl: são sete colunas, e a última é a única
          ação da tela. Em 5xl a tabela passava da caixa e "Status" e
          "Ações" ficavam fora do campo de visão — alcançáveis só por
          uma rolagem lateral que o macOS nem desenha até você tentar. */}
      <div className="mx-auto max-w-7xl p-4 sm:p-6">
        {/* Os filtros ficam aqui, sobre a tabela que eles filtram, e não no
            cabeçalho: são dois selects com rótulo, altos demais pra linha do
            título, e no celular espremiam o nome da tela contra o avatar. */}
        {/* Duas colunas de largura igual no celular. Com `w-44` fixo os dois
            filtros empilhavam em duas linhas ocupando pouco mais da metade da
            largura cada, e sobrava meia tela vazia à direita de ambos. */}
        <div className="mb-4 grid grid-cols-2 items-end gap-3 sm:flex sm:flex-wrap">
          <div className="min-w-0 space-y-1.5">
            <Label htmlFor="fila-prof">Profissional</Label>
            <Select value={professionalFilter} onValueChange={setProfessionalFilter}>
              <SelectTrigger id="fila-prof" className="w-full sm:w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos</SelectItem>
                {professionals.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-0 space-y-1.5">
            <Label htmlFor="fila-servico">Serviço</Label>
            <Select value={serviceFilter} onValueChange={setServiceFilter}>
              <SelectTrigger id="fila-servico" className="w-full sm:w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todos</SelectItem>
                {services.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          {waitlist.isPending ? (
            <Skeleton className="h-72 w-full" />
          ) : waitlist.isError ? (
            <p role="alert" className="text-sm text-destructive">
              {waitlist.error instanceof ApiError
                ? waitlist.error.message
                : "Não foi possível carregar a fila de espera."}
            </p>
          ) : (
            <>
              {removeError && (
                <p role="alert" className="mb-3 text-sm text-destructive">
                  {removeError}
                </p>
              )}

              {entries.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Nenhum cliente na fila. A fila é alimentada pelo bot do WhatsApp
                  quando não há horário livre.
                </p>
              ) : (
                <>
                  {/* Abaixo de `md` a tabela vira cartão. Não é preferência:
                    são sete colunas, e num telefone de 360 a tabela pedia
                    800px de rolagem lateral — o botão "Remover", única ação
                    da tela, ficava a três telas de distância do dedo. */}
                  <ul className="space-y-3 md:hidden">
                    {entries.map((entry) => {
                      const customerName = customerNames.get(entry.customer_id) ?? "…";
                      return (
                        <li
                          key={entry.id}
                          className="space-y-2 rounded-md border bg-card p-4"
                        >
                          <div className="flex items-start justify-between gap-3">
                            <Link
                              to="/clientes/$customerId"
                              params={{ customerId: entry.customer_id }}
                              className="min-w-0 flex-1 text-sm font-medium underline-offset-4 hover:underline"
                            >
                              {customerName}
                            </Link>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="-mr-2 -mt-1 h-9 shrink-0 gap-1.5 px-2 text-xs text-destructive sm:h-7"
                              onClick={() => {
                                setRemoveError(null);
                                setConfirming(entry);
                              }}
                              aria-label={`Remover ${customerName} da fila`}
                            >
                              <Trash2 className="h-3 w-3" />
                              Remover
                            </Button>
                          </div>

                          <p className="text-sm text-muted-foreground">
                            {serviceNames.get(entry.service_id) ?? "…"} ·{" "}
                            {entry.professional_id
                              ? (professionalNames.get(entry.professional_id) ?? "…")
                              : "Qualquer profissional"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {formatDate(entry.period_start)} a{" "}
                            {formatDate(entry.period_end)}
                          </p>

                          <div className="flex flex-wrap items-center gap-2 pt-0.5">
                            {entry.appointment_id ? (
                              <Badge variant="outline">
                                Vaga oferecida — aguardando resposta
                              </Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">
                                Aguardando vaga
                              </span>
                            )}
                            <span className="text-xs text-muted-foreground">
                              · entrou em {formatInstant(entry.created_at, timeZone)}
                            </span>
                          </div>
                        </li>
                      );
                    })}
                  </ul>

                  <div className="hidden rounded-md border bg-card md:block">
                    {/* Sem `min-w`: a tabela só precisa rolar quando o
                      conteúdo não couber de verdade, e o container do
                      próprio Table já cuida disso. */}
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Cliente</TableHead>
                          <TableHead>Serviço</TableHead>
                          <TableHead>Profissional</TableHead>
                          <TableHead>Período</TableHead>
                          <TableHead>Entrada em</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {entries.map((entry) => {
                          const customerName =
                            customerNames.get(entry.customer_id) ?? "…";
                          return (
                            <TableRow key={entry.id}>
                              <TableCell className="max-w-56 font-medium">
                                {/* A ficha do cliente é a porta de entrada (o
                                  painel ainda não tem listagem geral de
                                  clientes). */}
                                <Link
                                  to="/clientes/$customerId"
                                  params={{ customerId: entry.customer_id }}
                                  className="block truncate underline-offset-4 hover:underline"
                                  title={customerName}
                                >
                                  {customerName}
                                </Link>
                              </TableCell>
                              <TableCell>
                                {serviceNames.get(entry.service_id) ?? "…"}
                              </TableCell>
                              <TableCell>
                                {entry.professional_id
                                  ? (professionalNames.get(entry.professional_id) ??
                                    "…")
                                  : "Qualquer"}
                              </TableCell>
                              <TableCell>
                                {formatDate(entry.period_start)} a{" "}
                                {formatDate(entry.period_end)}
                              </TableCell>
                              <TableCell>
                                {formatInstant(entry.created_at, timeZone)}
                              </TableCell>
                              <TableCell>
                                {/* Quem confirma a vaga oferecida é o cliente, no
                                  WhatsApp — o painel não converte manualmente. */}
                                {entry.appointment_id ? (
                                  <Badge variant="outline">
                                    Vaga oferecida — aguardando resposta
                                  </Badge>
                                ) : (
                                  <span className="text-muted-foreground">
                                    Aguardando vaga
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-7 gap-1.5 px-2 text-xs text-destructive"
                                  onClick={() => {
                                    setRemoveError(null);
                                    setConfirming(entry);
                                  }}
                                  aria-label={`Remover ${customerName} da fila`}
                                >
                                  <Trash2 className="h-3 w-3" />
                                  Remover
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      <ResponsiveDialog
        open={confirming !== null}
        onOpenChange={(open) => {
          if (!open && !removing) setConfirming(null);
        }}
      >
        <ResponsiveDialogContent>
          <ResponsiveDialogHeader>
            <ResponsiveDialogTitle>Remover da fila de espera?</ResponsiveDialogTitle>
            <ResponsiveDialogDescription>
              {confirming
                ? `${customerNames.get(confirming.customer_id) ?? "O cliente"} deixa de receber ofertas de ${serviceNames.get(confirming.service_id) ?? "serviço"} para o período de ${formatDate(confirming.period_start)} a ${formatDate(confirming.period_end)}. Não dá para desfazer.`
                : ""}
            </ResponsiveDialogDescription>
          </ResponsiveDialogHeader>
          <ResponsiveDialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirming(null)}
              disabled={removing}
            >
              Cancelar
            </Button>
            <Button variant="destructive" onClick={onRemove} disabled={removing}>
              {removing ? "Removendo…" : "Remover"}
            </Button>
          </ResponsiveDialogFooter>
        </ResponsiveDialogContent>
      </ResponsiveDialog>
    </>
  );
}
